<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Liability</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <form class="px-6 pb-5 pt-2" @submit.prevent="generateReport">
      <div class="flex flex-col md:flex-row md:items-end md:flex-wrap gap-x-4 gap-y-2">
        <div class="flex flex-col gap-1 w-full md:w-auto md:min-w-[180px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">From</label>
          <input
            v-model="form.fromDate"
            type="date"
            class="px-2.5 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          />
        </div>

        <div class="flex flex-col gap-1 w-full md:w-auto md:min-w-[180px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Upto</label>
          <input
            v-model="form.uptoDate"
            type="date"
            class="px-2.5 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          />
        </div>

        <div class="flex flex-col gap-1 w-full md:w-auto">
          <button
            type="submit"
            :disabled="isLoadingReport"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span v-if="isLoadingReport" class="inline-flex items-center gap-2">
              <span class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
              Generating...
            </span>
            <span v-else>Generate</span>
          </button>
        </div>
      </div>
    </form>

    <div class="px-2 pb-6 pt-2">
      <template v-if="showReportTable">
        <div v-if="isLoadingReport" class="py-12 flex justify-center items-center">
          <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else class="w-full">
          <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0 px-4">
            {{ reportPeriodTitle }}
          </p>

          <div class="liability-table-scroll relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)] px-2 custom-scrollbar">
            <table class="w-full liability-table border-collapse">
              <thead>
                <tr>
                  <th class="col-sn">No.</th>
                  <th class="col-date">Date</th>
                  <th class="col-company">Company Name</th>
                  <th class="col-source">Source</th>
                  <th class="num">Amount</th>
                  <th class="col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in reportRows"
                  :key="'liability-' + row.id"
                  class="hover:bg-slate-50 transition-colors border-b"
                >
                  <td class="col-sn">{{ idx + 1 }}</td>
                  <td class="col-date">{{ formatDisplayDate(row.postingDate) }}</td>
                  <td class="col-company">{{ row.companyName || '—' }}</td>
                  <td class="col-source">{{ row.source || '—' }}</td>
                  <td class="num font-semibold text-[#0d4169]">{{ fmt(row.amount) }}</td>
                  <td class="col-action actions">
                    <button
                      type="button"
                      class="text-emerald-600 edit-btn hover:underline"
                      :disabled="isLoadingDetail && selectedDetailRowId === row.id"
                      @click="showRowDetail(row)"
                    >
                      <span v-if="isLoadingDetail && selectedDetailRowId === row.id">Loading...</span>
                      <span v-else>Detail</span>
                    </button>
                  </td>
                </tr>
                <tr v-if="reportRows.length === 0">
                  <td colspan="6" class="text-center text-slate-400 italic py-5">
                    No liabilities found for the selected filters.
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="reportRows.length > 0">
                <tr class="font-semibold liability-tfoot-row">
                  <td colspan="4" class="liability-total-label">Total</td>
                  <td class="num font-semibold text-[#0d4169]">{{ fmt(totals.amount) }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>

      <p
        v-else
        class="text-sm text-slate-500 text-center py-10 m-0 italic"
      >
        Select optional date filters and click Generate to view liabilities.
      </p>
    </div>
  </div>

<div v-if="isDetailModalOpen && selectedRowDetail">
  <div class="liability-modal-backdrop" @click.self="isDetailModalOpen = false">
    <div class="liability-modal">
      <div class="liability-modal-header">
        <div>
          <p class="liability-modal-title">
            {{ selectedRowDetail.source || 'Liability' }} Detail
          </p>
          <p class="liability-modal-subtitle">
            Ref: {{ (selectedRowHeader && selectedRowHeader.reference) || selectedRowDetail.reference || '—' }}
            ·
            Date: {{ formatDisplayDate((selectedRowHeader && selectedRowHeader.postingDate) || selectedRowDetail.postingDate) }}
          </p>
        </div>
        <button type="button" class="liability-modal-close" @click="isDetailModalOpen = false">
          ✕
        </button>
      </div>

      <div class="liability-modal-body">
        <div class="liability-summary-grid">
          <div class="liability-summary-item">
            <span class="label">Supplier / Company</span>
            <span class="value">
              {{
                (selectedRowHeader && selectedRowHeader.supplierName) ||
                (selectedRowHeader && selectedRowHeader.sellerName) ||
                selectedRowDetail.companyName ||
                '—'
              }}
            </span>
          </div>

          <div class="liability-summary-item" v-if="selectedRowDetail.entryType === 'payment_purchase'">
            <span class="label">CPV Number</span>
            <span class="value">{{ (selectedRowHeader && selectedRowHeader.cpvNumber) || '—' }}</span>
          </div>

          <div class="liability-summary-item" v-if="selectedRowDetail.entryType === 'payment_purchase'">
            <span class="label">Payment Type</span>
            <span class="value">{{ (selectedRowHeader && selectedRowHeader.paymentType) || '—' }}</span>
          </div>

          <div class="liability-summary-item" v-if="selectedRowDetail.entryType === 'payment_purchase'">
            <span class="label">Receipt</span>
            <span class="value">
              {{ (selectedRowHeader && selectedRowHeader.receiptNumber) || '—' }}
              <span v-if="selectedRowHeader && selectedRowHeader.receiptDate">
                ({{ formatDisplayDate(selectedRowHeader.receiptDate) }})
              </span>
            </span>
          </div>

          <div class="liability-summary-item">
            <span class="label">Liability Amount</span>
            <span class="value highlight">{{ fmt(selectedRowDetail.amount) }}</span>
          </div>

          <div class="liability-summary-item" v-if="selectedRowDetail.entryType !== 'payment_purchase'">
            <span class="label">Document Total</span>
            <span class="value">
              {{ fmt((selectedRowHeader && selectedRowHeader.totalAmount) || selectedRowDetail.amount) }}
            </span>
          </div>

          <div class="liability-summary-item" v-if="selectedRowDetail.entryType === 'payment_purchase'">
            <span class="label">Amount in Figures</span>
            <span class="value">
              {{ fmt((selectedRowHeader && selectedRowHeader.amountInFigures) || 0) }}
            </span>
          </div>

          <div class="liability-summary-item" v-if="selectedRowDetail.entryType === 'payment_purchase'">
            <span class="label">Bank Amount Difference</span>
            <span class="value">
              {{ fmt((selectedRowHeader && selectedRowHeader.bankAmountDifference) || 0) }}
            </span>
          </div>
        </div>

        <div
          v-if="selectedRowHeader && selectedRowHeader.purposeOfPayment && selectedRowDetail.entryType === 'payment_purchase'"
          class="liability-purpose"
        >
          <span class="label">Purpose of Payment:</span>
          <span class="value">{{ selectedRowHeader.purposeOfPayment }}</span>
        </div>

        <div class="liability-detail-table-wrap">
          <table class="w-full liability-table border-collapse">
            <thead>
              <tr>
                <th class="col-sn">No.</th>
                <th class="col-company">Item</th>
                <th class="num">Qty</th>
                <th class="num">Unit Price</th>
                <th class="num">Total Price</th>
                <th class="num">VAT</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(detail, idx) in selectedRowDetails"
                :key="'detail-' + detail.id"
                class="hover:bg-slate-50 transition-colors border-b"
              >
                <td class="col-sn">{{ idx + 1 }}</td>
                <td class="col-company">{{ detail.itemName || '—' }}</td>
                <td class="num">{{ fmt(detail.qty) }}</td>
                <td class="num">{{ fmt(detail.unitPrice) }}</td>
                <td class="num">{{ fmt(detail.totalPrice) }}</td>
                <td class="num">{{ fmt(detail.vat) }}</td>
              </tr>
              <tr v-if="selectedRowDetails.length === 0">
                <td colspan="6" class="text-center text-slate-400 italic py-5">
                  No detail rows found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="liability-modal-footer">
        <button type="button" class="modal-close-btn" @click="isDetailModalOpen = false">
          Close
        </button>
      </div>
    </div>
  </div>
</div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'
import FinanceToast from '../FinanceToast.vue'

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = reactive({
  fromDate: '',
  uptoDate: ''
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

const reportRows = ref([])
const isLoadingReport = ref(false)
const isLoadingDetail = ref(false)
const selectedDetailRowId = ref('')
const hasGenerated = ref(false)
const selectedRowDetail = ref(null)
const selectedRowDetails = ref([])
const selectedRowHeader = ref(null)
const isDetailModalOpen = ref(false)

const showReportTable = computed(() => hasGenerated.value)

function toLocalYmd(dateObj) {
  const y = dateObj.getFullYear()
  const m = String(dateObj.getMonth() + 1).padStart(2, '0')
  const d = String(dateObj.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatDisplayDate(rawDate) {
  const value = String(rawDate || '').trim()
  if (!value) return '—'
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return toLocalYmd(parsed)
}

const reportPeriodTitle = computed(() => {
  if (form.fromDate && form.uptoDate) {
    return `Liabilities for ${form.fromDate} - ${form.uptoDate}`
  }
  if (form.fromDate) {
    return `Liabilities from ${form.fromDate}`
  }
  if (form.uptoDate) {
    return `Liabilities up to ${form.uptoDate}`
  }
  return 'Liabilities (all dates)'
})

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function fmt(x) {
  return n(x).toFixed(2)
}

const totals = computed(() => ({
  amount: reportRows.value.reduce((acc, row) => acc + n(row?.amount), 0)
}))

function normalizeDateFilter(fromDate, uptoDate) {
  const from = String(fromDate || '').trim()
  const upto = String(uptoDate || '').trim()
  if (from && upto) return { fromDate: from, toDate: upto }
  if (from && !upto) return { fromDate: from, toDate: from }
  if (!from && upto) return { fromDate: upto, toDate: upto }
  return { fromDate: '', toDate: '' }
}

async function fetchLiabilityReport(fromDate, toDate) {
  const qs = new URLSearchParams()
  if (fromDate) qs.set('fromDate', fromDate)
  if (toDate) qs.set('toDate', toDate)
  const url = qs.toString() ? `/finance/liability-report?${qs.toString()}` : '/finance/liability-report'
  const res = await apiFetch(url)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to load liability report.')
  }
  return res.json()
}

async function fetchLiabilityDetail(entryType, sourceId) {
  const res = await apiFetch(`/finance/liability-report/${entryType}/${sourceId}/details`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to load liability detail.')
  }
  return res.json()
}

async function showRowDetail(row) {
  if (!row?.entryType || !row?.sourceId) {
    showToast('Invalid row detail request.', 'error')
    return
  }
  selectedDetailRowId.value = row.id
  isLoadingDetail.value = true
  try {
    const data = await fetchLiabilityDetail(row.entryType, row.sourceId)
    selectedRowDetail.value = row
    selectedRowHeader.value = data?.header || null
    selectedRowDetails.value = Array.isArray(data?.details) ? data.details : []
    isDetailModalOpen.value = true
  } catch (error) {
    selectedRowDetail.value = null
    selectedRowDetails.value = []
    selectedRowHeader.value = null
    isDetailModalOpen.value = false
    showToast(error?.message || 'Failed to load liability detail.', 'error')
  } finally {
    isLoadingDetail.value = false
    selectedDetailRowId.value = ''
  }
}

async function generateReport() {
  const fromDate = String(form.fromDate || '').trim()
  const uptoDate = String(form.uptoDate || '').trim()
  if (fromDate && uptoDate && uptoDate < fromDate) {
    showToast('Upto date cannot be before From date.', 'error')
    return
  }

  isLoadingReport.value = true
  hasGenerated.value = true
  selectedRowDetail.value = null
  selectedRowDetails.value = []
  selectedRowHeader.value = null
  isDetailModalOpen.value = false

  try {
    const normalized = normalizeDateFilter(fromDate, uptoDate)
    const data = await fetchLiabilityReport(normalized.fromDate, normalized.toDate)
    reportRows.value = Array.isArray(data?.rows) ? data.rows : []
  } catch (error) {
    reportRows.value = []
    showToast(error?.message || 'Failed to load liability report.', 'error')
  } finally {
    isLoadingReport.value = false
  }
}
</script>

<style scoped>
.liability-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  width: 100%;
}

.liability-table th,
.liability-table td {
  padding: 6px 12px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
  width: calc(100% / 6);
}

.liability-table th {
  background: #f1f5f9;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.liability-table th.col-sn,
.liability-table td.col-sn,
.liability-table th.col-date,
.liability-table td.col-date,
.liability-table th.col-company,
.liability-table td.col-company,
.liability-table th.col-source,
.liability-table td.col-source,
.liability-table th.col-action,
.liability-table td.col-action {
  text-align: left;
}

.col-date,
.col-source {
  white-space: nowrap;
}

.col-company {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.liability-table th.num,
.liability-table td.num {
  text-align: center;
  white-space: nowrap;
}

.liability-table tbody tr:hover td {
  background-color: #f8fafc;
}

.liability-table tfoot td {
  background: #f1f5f9;
  border-top: 2px solid #cbd5e1;
  font-weight: 600;
}

.liability-table tfoot td.liability-total-label {
  text-align: left;
}

.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 11px;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions .edit-btn {
  font-size: 0.85rem;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.liability-modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  overflow-y: auto;
  z-index: 200;
}

.liability-modal {
  background: #ffffff;
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.35);
  max-width: 960px;
  width: 100%;
  max-height: calc(100vh - 4rem);
  margin: auto 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.liability-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #0a3354;
  background: #0d4169;
}

.liability-modal-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
}

.liability-modal-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.75rem;
  color: #dbeafe;
}

.liability-modal-close {
  border: none;
  background: transparent;
  font-size: 0.95rem;
  color: #ffffff;
  cursor: pointer;
  padding: 0.2rem 0.35rem;
  border-radius: 9999px;
  line-height: 1;
}

.liability-modal-close:hover {
  background: rgba(255, 255, 255, 0.12);
}

.liability-modal-body {
  padding: 1rem 1.5rem 0.75rem;
  overflow: auto;
}

.liability-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem 1rem;
  margin-bottom: 0.75rem;
}

.liability-summary-item .label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
}

.liability-summary-item .value {
  display: block;
  font-size: 0.78rem;
  color: #111827;
}

.liability-summary-item .value.highlight {
  color: #0d4169;
  font-weight: 700;
}

.liability-purpose {
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  font-size: 0.78rem;
}

.liability-purpose .label {
  font-weight: 600;
  color: #4b5563;
  margin-right: 0.25rem;
}

.liability-purpose .value {
  color: #111827;
}

.liability-detail-table-wrap {
  max-height: 260px;
  overflow: auto;
}

.liability-modal-footer {
  padding: 0.75rem 1.5rem 1rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.modal-close-btn {
  padding: 0.4rem 1rem;
  font-size: 0.8rem;
  border-radius: 0.375rem;
  border: 1px solid #94a3b8;
  background: #f8fafc;
  color: #334155;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.modal-close-btn:hover {
  background: #e5e7eb;
}
</style>
